import {
  ChassisDamageLocation,
  ChassisDamageTypes,
  ChassisLocationPart,
  chassisLocationPartMap
} from './chassis-types';
import { ContainerDamageTypes, ContainerLocationPart, containerSystems } from './container-types';
import { DamageType, EquipmentType } from './damage-types';

class GenericDamage {
  id?: number;
  equipmentType?: EquipmentType;
  damageType?: DamageType;
  notes?: string;
  imageBase64?: string;
  imageKey?: string;
}

export class TransactionDamage extends GenericDamage {
  id: number = 0;
  location?: string;
  subLocation?: string;
  part?: string;
}

export class ChassisDamage<
  P extends keyof ChassisDamageTypes | undefined = keyof ChassisDamageTypes | undefined,
  Q = P extends keyof ChassisDamageTypes ? keyof ChassisDamageTypes[P] | undefined : undefined
> extends GenericDamage {
  equipmentType?: EquipmentType.CHASSIS = EquipmentType.CHASSIS;
  location?: P;
  part?: Q;
  subLocation?: string;
}

export class ContainerDamage<
  P extends keyof ContainerDamageTypes | undefined = keyof ContainerDamageTypes | undefined,
  Q = P extends keyof ContainerDamageTypes ? keyof ContainerDamageTypes[P] | undefined : undefined
> extends GenericDamage {
  equipmentType?: EquipmentType.CONTAINER = EquipmentType.CONTAINER;
  location?: P;
  part?: Q;
}

export class GensetDamage<P extends string | undefined = string | undefined> extends GenericDamage {
  equipmentType?: EquipmentType.GENSET = EquipmentType.GENSET;
  location?: P;
}

export type Damage = ChassisDamage | ContainerDamage | GensetDamage;

export const isChassisDamage = <P extends Damage>(d: P): d is P & ChassisDamage =>
  d.equipmentType === EquipmentType.CHASSIS;

export const isContainerDamage = <P extends Damage>(d: P): d is P & ContainerDamage =>
  d.equipmentType === EquipmentType.CONTAINER;

export const isGensetDamage = <P extends Damage>(d: P): d is P & GensetDamage =>
  d.equipmentType === EquipmentType.GENSET;

export const chassisDamageHasLocation = <P extends ChassisDamage>(
  d: P
): d is P & { location: keyof ChassisDamageTypes } => !!d.location && d.location in chassisLocationPartMap;

export const chassisDamageWithLocationHasPart = <
  P extends ChassisDamage<keyof ChassisDamageTypes> & { location: keyof ChassisDamageTypes }
>(
  d: P
): d is P & { part: ChassisLocationPart[P['location']] } =>
  !!d.part && !!d.part.length

export const containerDamageHasLocation = <P extends ContainerDamage>(
  d: P
): d is P & { location: keyof ContainerDamageTypes } => !!d.location && d.location in containerSystems;

export const containerDamageWithLocationHasPart = <
  P extends ContainerDamage<keyof ContainerDamageTypes> & { location: keyof ContainerDamageTypes }
>(
  d: P
): d is P & { part: ContainerLocationPart[P['location']] } => !!d.part && !!d.part?.length;

export const convertSpecialCharacters = (value: any): any => {
  switch (value) {
    case DamageType.BURNED_OUT:
      return 'BURNED_OUT';
    case DamageType.CORRODED_OR_ROTTED:
      return 'CORRODED_OR_ROTTED';
    case DamageType.FROZEN_OR_SEIZED:
      return 'FROZEN_OR_SEIZED';
    case ChassisDamageLocation.AIR_SYSTEM:
      return 'AIR_SYSTEM';
    case ChassisDamageLocation.LANDING_GEAR:
      return 'LANDING_GEAR';
    default:
      return value;
  }
};

export const restoreSpecialCharacters = (value: any): any => {
  switch (value) {
    case 'BURNED_OUT':
      return DamageType.BURNED_OUT;
    case 'CORRODED_OR_ROTTED':
      return DamageType.CORRODED_OR_ROTTED;
    case 'FROZEN_OR_SEIZED':
      return DamageType.FROZEN_OR_SEIZED;
    case 'AIR_SYSTEM':
      return ChassisDamageLocation.AIR_SYSTEM;
    case 'LANDING_GEAR':
      return ChassisDamageLocation.LANDING_GEAR;
    default:
      return value;
  }
};
